import React from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';

import { constants as destinationConstants } from '@eb/destination-utils';
import { trackAnalyticsEvent } from '@eb/site-analytics';

import { getWindowObject } from 'js-utils/feature-detection';
import Layout from 'eventbrite_design_system/layout/Layout';

import {
    BUCKETS_PROPTYPES,
    FEATURE_FLAGS_PROPTYPES,
    ORGANIZER_ARTICLE_BUCKET_PROPTYPE,
    FLAT_BUCKET_PROPTYPE,
} from '../constants/propTypes';
import {
    GA_HOME_FEED_CATEGORY,
    GA_VIEW_HOMEPAGE_FEED,
    GA_VIEW_LOGGEDOUT_HOMEPAGE_FEED,
    GA_HOME_CATEGORY,
} from '../constants/analytics';
import { storeHeroImageIndex } from '../utils/localStorage';
import ConnectedHeader from '../containers/ConnectedHeader';
import ConnectedBasePage from './ConnectedBasePage';
import BrowseContainer from '../containers/BrowseContainer';
import { LUX_LABEL_FEED } from '../constants/constants';

import CoronavirusBanner from '../components/CoronavirusBanner';
import EnhancedHomeConsumerHeader from '../components/EnhancedHomeConsumerHeader';

const ProtestMap = loadable(() =>
    import(/* webpackPrefetch: true */ '../components/ProtestMap/ProtestMap'),
);

const { PLACE_OBJECT_PROPTYPE } = destinationConstants;

const trackSpeedCurveLUX = () => {
    const LUX = getWindowObject('LUX');

    if (LUX) {
        LUX.label = LUX_LABEL_FEED;
    }
};

const applyIEFixes = () => {
    /**
     * IE is returning errors because SVG elements are not correctly mapped when parsing the docs.
     * This post gives a solution to this: https://github.com/Pomax/react-onclickoutside#ie-does-not-support-classlist-for-svg-elements
     */
    if (!('classList' in SVGElement.prototype)) {
        Object.defineProperty(SVGElement.prototype, 'classList', {
            get() {
                return {
                    contains: (className) =>
                        this.className.baseVal.split(' ').indexOf(className) !==
                        -1,
                };
            },
        });
    }
};

// Deal with the messed up Varnish urls (/esi_cahe/?...) which break the signin link
const fixLoginUrl = (loginUrl) => {
    const splitUrl = loginUrl.split('/');
    const urlBeginning = splitUrl.slice(0, -1);

    return urlBeginning.concat('?referrer=%2F').join('/');
};

export default class FeedPage extends React.Component {
    static propTypes = {
        // feed props
        buckets: BUCKETS_PROPTYPES,
        featureFlags: FEATURE_FLAGS_PROPTYPES,
        placeId: PropTypes.string,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        currentPlace: PropTypes.string,
        currentPlaceParent: PropTypes.string,
        locations: PropTypes.shape({
            places: PropTypes.arrayOf(PLACE_OBJECT_PROPTYPE),
        }),
        placeType: PropTypes.string,
        slug: PropTypes.string,
        fiveToFollow: PropTypes.arrayOf(ORGANIZER_ARTICLE_BUCKET_PROPTYPE),
        flatBucket: FLAT_BUCKET_PROPTYPE,
        isAuthenticated: PropTypes.bool,
        userId: PropTypes.string,
        handleRestOfContext: PropTypes.func,
        originalContext: PropTypes.object,
        history: PropTypes.object,
        env: PropTypes.object,
    };

    static defaultProps = {
        handleRestOfContext: () => null,
    };

    state = {
        isInBrowser: false,
    };

    componentDidMount() {
        const {
            isAuthenticated,
            userId,
            handleRestOfContext,
            originalContext,
        } = this.props;

        let category = GA_HOME_CATEGORY;

        let action = GA_VIEW_LOGGEDOUT_HOMEPAGE_FEED;

        let extraAttributes = {};

        if (isAuthenticated) {
            category = GA_HOME_FEED_CATEGORY;
            action = GA_VIEW_HOMEPAGE_FEED;
            extraAttributes = { dimensions: { userId } };
        }

        trackSpeedCurveLUX();

        applyIEFixes();

        trackAnalyticsEvent({
            category,
            action,
            nonInteraction: true,
            ...extraAttributes,
        });

        handleRestOfContext(originalContext);

        // Updates the last hero image seen to get a new one.
        storeHeroImageIndex();

        this.setState({ isInBrowser: true });
    }

    render() {
        // During the server render, because of Varnish, we want to render the page
        // as a logged-out user. Once we are in the browser, we have the props for the user
        // and we want to update the page. Since most of our components do not keep this in mind,
        // we basically have to do it with componentDidMount or else we get weird markup mismatch issues.
        // Once we're in the browser, we'll go to use the normal props.
        // See also the ConsumerHeader/index.js
        let overrideProps = this.state.isInBrowser
            ? {}
            : {
                  env: {
                      ...this.props.env,
                      loginUrl: fixLoginUrl(this.props.env.loginUrl),
                      showCookieHeader: false,
                  },
                  footer: <div />,
                  user: {
                      isAuthenticated: false,
                      publicId: null,
                      canCreateEvents: true,
                  },
              };

        const isDotCom = this.props.env.serverUrl.endsWith('.com');
        return (
            <ConnectedBasePage
                {...this.props}
                {...overrideProps}
                isHomePage={true}
                structureHeader={EnhancedHomeConsumerHeader}
            >
                <Layout hasHorizontalGutters={true} maxWidth="large">
                    {isDotCom ? (
                        <ProtestMap env={this.props.env} />
                    ) : (
                        <>
                            <CoronavirusBanner />
                            <ConnectedHeader history={this.props.history} />
                        </>
                    )}
                    <BrowseContainer />
                </Layout>
            </ConnectedBasePage>
        );
    }
}
